"use client"

import { ScreenNames } from "config/routing"
import { selectActivePpSubId, useAppSelector } from "services"
import useDashboardRoutes from "../useDashboardRoutes/useDashboardRoutes"
import useFeatureRoutes from "../useFeatureRoutes/useFeatureRoutes"
import useSalesRoutes from "../useSalesRoutes"

interface IPPRoutes {
    // Static routes for comparison
    staticRoutes: {
        [key: string]: string
    }
    // Root
    analytics: () => string
    editKeg: (kegId: string) => string
    inventory: () => string
    messageCenter: () => string
    onDeck: () => string
    onTap: () => string
    receiveKeg: () => string
    resolveKeg: () => string
    tapSensor: () => string
    // Settings
    batteryReplacement: () => string
    brandingDetails: () => string
    brewKnowledgeSettings: () => string
    draftProgram: () => string
    freshness: () => string
    general: () => string
    locationDetails: () => string
    pPPreferences: () => string
    pricing: () => string
    priorityBrands: () => string
    settings: () => string
    workWeekAndHours: () => string
    // Analytics
    breweries: () => string
    brews: () => string
    brewStats: () => string
    depletionAnalysis: () => string
    freshnessAnalysis: () => string
    history: () => string
    kickDateAnalysis: () => string
    levelAnalysis: () => string
    stylesOnTap: () => string
    throughput: () => string
    // Dashboard, Root
    currentPPDashboard: () => string
    dashboard: (ppSubId: string) => string
    ppRoot: () => string
    ppSales: () => string
    preferences: () => string
    varianceReport: () => string
}

// Operations
const onTapRoute = `${ScreenNames.OnTap}`
const onDeckRoute = `${ScreenNames.OnDeck}`
const inventoryRoute = `${ScreenNames.Inventory}`
const editKegRoute = `${ScreenNames.Inventory}/${ScreenNames.EditKeg}`
const receiveKegRoute = `${ScreenNames.Inventory}/${ScreenNames.ReceiveKeg}`
const resolveKegRoute = `${ScreenNames.Inventory}/${ScreenNames.ResolveKeg}`
const analyticsRoute = `${ScreenNames.Apps}/${ScreenNames.Analytics}`
const tapSensorRoute = `${ScreenNames.OnDeck}/${ScreenNames.TapSensor}`
const messageCenterRoute = `${ScreenNames.MessageCenter}`

// Operations - Settings
const settingsRoute = `${ScreenNames.Settings}`
const locationDetailsRoute = `${ScreenNames.Settings}/${ScreenNames.LocationDetails}`
const brandingDetailsRoute = `${ScreenNames.Settings}/${ScreenNames.BrandingDetails}`
const workWeekAndHoursRoute = `${ScreenNames.Settings}/${ScreenNames.WorkWeekAndHours}`
const pricingRoute = `${ScreenNames.Settings}/${ScreenNames.Pricing}`
const priorityBrandsRoute = `${ScreenNames.Settings}/${ScreenNames.PriorityBrands}`
const draftProgramRoute = `${ScreenNames.Settings}/${ScreenNames.DraftProgram}`
const freshnessRoute = `${ScreenNames.Settings}/${ScreenNames.Freshness}`
const batteryReplacementRoute = `${ScreenNames.Settings}/${ScreenNames.BatteryReplacement}`
const brewKnowledgeSettingsRoute = `${ScreenNames.Settings}/${ScreenNames.BrewKnowledge}`
const generalRoute = `${ScreenNames.Settings}/${ScreenNames.General}`
const pPPreferencesRoute = `${ScreenNames.Settings}/${ScreenNames.Preferences}`

// Operations - Analytics
const throughputRoute = `${ScreenNames.Analytics}/${ScreenNames.Throughput}`
const levelAnalysisRoute = `${ScreenNames.Analytics}/${ScreenNames.LevelAnalysis}`
const depletionAnalysisRoute = `${ScreenNames.Analytics}/${ScreenNames.DepletionAnalysis}`
const kickDateAnalysisRoute = `${ScreenNames.Analytics}/${ScreenNames.KickDateAnalysis}`
const freshnessAnalysisRoute = `${ScreenNames.Analytics}/${ScreenNames.FreshnessAnalysis}`
const stylesOnTapRoute = `${ScreenNames.Analytics}/${ScreenNames.StylesOnTap}`
const brewStatsRoute = `${ScreenNames.Analytics}/${ScreenNames.BrewStats}`
const historyRoute = `${ScreenNames.Analytics}/${ScreenNames.History}`
const brewsRoute = `${ScreenNames.Analytics}/${ScreenNames.History}/${ScreenNames.Brews}`
const breweriesRoute = `${ScreenNames.Analytics}/${ScreenNames.History}/${ScreenNames.Breweries}`
const varianceReportRoute = `${ScreenNames.Analytics}/${ScreenNames.VarianceReport}`

const usePPRoutes = (): IPPRoutes => {
    const activePPSubId = useAppSelector(selectActivePpSubId)

    const { performancePlatformFeatureRoute } = useFeatureRoutes()
    const { ppDashboard: ppDashboardRoot } = useDashboardRoutes()
    const { ppSales } = useSalesRoutes()

    // Given a ppSub in state: Route is built as ROOT (performancePlatformFeatureRoute) & PP_SUB_ID & LINK
    // Given no ppSub in state: Route is ROOT (performancePlatformFeatureRoute)
    const ppSubWrapper = (link) =>
        activePPSubId
            ? `${performancePlatformFeatureRoute()}/${link}`
            : performancePlatformFeatureRoute()

    // Current URLs: Root
    const analytics = () => ppSubWrapper(analyticsRoute)
    const editKeg = (kegId: string) => ppSubWrapper(`${editKegRoute}/${kegId}`)
    const inventory = () => ppSubWrapper(inventoryRoute)
    const messageCenter = () => ppSubWrapper(messageCenterRoute)
    const onDeck = () => ppSubWrapper(onDeckRoute)
    const onTap = () => ppSubWrapper(onTapRoute)
    const receiveKeg = () => ppSubWrapper(receiveKegRoute)
    const resolveKeg = () => ppSubWrapper(resolveKegRoute)
    const tapSensor = () => ppSubWrapper(tapSensorRoute)

    // Current URLs: Settings
    const batteryReplacement = () => ppSubWrapper(batteryReplacementRoute)
    const brandingDetails = () => ppSubWrapper(brandingDetailsRoute)
    const brewKnowledgeSettings = () => ppSubWrapper(brewKnowledgeSettingsRoute)
    const draftProgram = () => ppSubWrapper(draftProgramRoute)
    const freshness = () => ppSubWrapper(freshnessRoute)
    const general = () => ppSubWrapper(generalRoute)
    const locationDetails = () => ppSubWrapper(locationDetailsRoute)
    const pPPreferences = () => ppSubWrapper(pPPreferencesRoute)
    const pricing = () => ppSubWrapper(pricingRoute)
    const priorityBrands = () => ppSubWrapper(priorityBrandsRoute)
    const settings = () => ppSubWrapper(settingsRoute)
    const workWeekAndHours = () => ppSubWrapper(workWeekAndHoursRoute)

    // Curent URLs: Analytics
    const throughput = () => ppSubWrapper(throughputRoute)
    const levelAnalysis = () => ppSubWrapper(levelAnalysisRoute)
    const depletionAnalysis = () => ppSubWrapper(depletionAnalysisRoute)
    const kickDateAnalysis = () => ppSubWrapper(kickDateAnalysisRoute)
    const freshnessAnalysis = () => ppSubWrapper(freshnessAnalysisRoute)
    const stylesOnTap = () => ppSubWrapper(stylesOnTapRoute)
    const brewStats = () => ppSubWrapper(brewStatsRoute)
    const history = () => ppSubWrapper(historyRoute)
    const brews = () => ppSubWrapper(brewsRoute)
    const breweries = () => ppSubWrapper(breweriesRoute)
    const varianceReport = () => ppSubWrapper(varianceReportRoute)

    const preferences = () => ""

    return {
        // Root
        onTap,
        onDeck,
        inventory,
        editKeg,
        receiveKeg,
        resolveKeg,
        analytics,
        tapSensor,
        messageCenter,
        // Settings
        settings,
        locationDetails,
        brandingDetails,
        workWeekAndHours,
        pricing,
        priorityBrands,
        draftProgram,
        freshness,
        batteryReplacement,
        brewKnowledgeSettings,
        general,
        pPPreferences,
        // Analytics
        throughput,
        levelAnalysis,
        depletionAnalysis,
        kickDateAnalysis,
        freshnessAnalysis,
        stylesOnTap,
        brewStats,
        history,
        brews,
        breweries,
        varianceReport,
        // Dashboard, Root
        currentPPDashboard: ppDashboardRoot,
        dashboard: ppDashboardRoot,
        ppRoot: performancePlatformFeatureRoute,
        ppSales,
        // Unknown URL
        preferences,
        // Static Routes
        staticRoutes: {
            throughputStatic: throughputRoute,
            analyticsStatic: analyticsRoute,
            batteryReplacementStatic: batteryReplacementRoute,
            brandingDetailsStatic: brandingDetailsRoute,
            breweriesStatic: breweriesRoute,
            brewKnowledgeSettingsStatic: brewKnowledgeSettingsRoute,
            brewsStatic: brewsRoute,
            brewStatsStatic: brewStatsRoute,
            depletionAnalysisStatic: depletionAnalysisRoute,
            draftProgramStatic: draftProgramRoute,
            editKegStatic: editKegRoute,
            freshnessAnalysisStatic: freshnessAnalysisRoute,
            freshnessStatic: freshnessRoute,
            generalStatic: generalRoute,
            historyStatic: historyRoute,
            inventoryStatic: inventoryRoute,
            kickDateAnalysisStatic: kickDateAnalysisRoute,
            levelAnalysisStatic: levelAnalysisRoute,
            locationDetailsStatic: locationDetailsRoute,
            messageCenterStatic: messageCenterRoute,
            onDeckStatic: onDeckRoute,
            onTapStatic: onTapRoute,
            pPPreferencesStatic: pPPreferencesRoute,
            pricingStatic: pricingRoute,
            priorityBrandsStatic: priorityBrandsRoute,
            receiveKegStatic: receiveKegRoute,
            resolveKegStatic: resolveKegRoute,
            settingsStatic: settingsRoute,
            stylesOnTapStatic: stylesOnTapRoute,
            tapSensorStatic: tapSensorRoute,
            workWeekAndHoursStatic: workWeekAndHoursRoute,
            varianceReportStatic: varianceReportRoute
        }
    }
}

export default usePPRoutes
